import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { formatPhoneDisplay } from "../utils/phone";

const initialFormData = {
  name: "",
  email: "",
  phone: "",
  aadhar: "",
  esi: "",
  password: "",
  team: ""
};

const MAX_SIGNATURE_SIZE_BYTES = 2 * 1024 * 1024;
const SIGNATURE_FILE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

const getSignatureUrl = (signature = "") => {
  if (!signature) return "";
  if (signature.startsWith("blob:") || signature.startsWith("data:") || signature.startsWith("http")) {
    return signature;
  }

  return `${API_BASE_URL}${signature.startsWith("/") ? signature : `/${signature}`}`;
};

export default function Engineers() {
  const [engineers, setEngineers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingEngineerId, setEditingEngineerId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [search, setSearch] = useState("");
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState("");
  const [originalSignature, setOriginalSignature] = useState("");
  const [drawnSignatureData, setDrawnSignatureData] = useState("");
  const [uploadMessage, setUploadMessage] = useState({ text: "", tone: "idle" });
  const [signatureModal, setSignatureModal] = useState(null);
  const [fullscreenPadOpen, setFullscreenPadOpen] = useState(false);
  const signaturePadRef = useRef(null);
  const fullscreenSignatureRef = useRef(null);

  const [formData, setFormData] = useState(initialFormData);
  const [dynamicTeams, setDynamicTeams] = useState([]);

  useEffect(() => {
    fetchEngineers();
    
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    axios.get(API_BASE_URL + "/api/admin-settings/system-settings", { headers })
      .then(res => {
        if (res.data && Array.isArray(res.data.teams)) {
          setDynamicTeams(res.data.teams);
        }
      })
      .catch(err => console.error("Error loading system settings teams:", err));
  }, []);

  useEffect(() => {
    if (!fullscreenPadOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [fullscreenPadOpen]);

  const fetchEngineers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/engineers");
      setEngineers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching engineers:", error);
      alert("Failed to load engineers");
      setEngineers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const compressSignatureFile = (file) =>
    new Promise((resolve) => {
      if (!file) {
        resolve(file);
        return;
      }

      const image = new Image();
      const objectUrl = URL.createObjectURL(file);

      image.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let { width, height } = image;
        const maxDimension = 1600;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(image, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          "image/jpeg",
          0.8
        );
        URL.revokeObjectURL(objectUrl);
      };

      image.onerror = () => {
        resolve(file);
        URL.revokeObjectURL(objectUrl);
      };

      image.src = objectUrl;
    });

  const handleSignatureChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!SIGNATURE_FILE_TYPES.includes(file.type)) {
      event.target.value = "";
      setSignatureFile(null);
      setUploadMessage({ text: "Only PNG, JPG, or JPEG signature images are allowed.", tone: "error" });
      return;
    }

    const preparedFile = await compressSignatureFile(file);

    if (preparedFile.size > MAX_SIGNATURE_SIZE_BYTES) {
      event.target.value = "";
      setSignatureFile(null);
      setUploadMessage({ text: "Signature image must be 2MB or smaller.", tone: "error" });
      return;
    }

    setSignatureFile(preparedFile);
    setDrawnSignatureData("");
    setSignaturePreview(URL.createObjectURL(preparedFile));
    setUploadMessage({ text: "Signature selected successfully.", tone: "success" });
  };

  const saveDrawnSignature = (padRef = signaturePadRef) => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      setUploadMessage({ text: "Draw a signature before saving it.", tone: "error" });
      return false;
    }

    const dataUrl = pad.getTrimmedCanvas().toDataURL("image/png");
    setDrawnSignatureData(dataUrl);
    setSignatureFile(null);
    setSignaturePreview(dataUrl);
    setUploadMessage({ text: "Drawn signature ready to save.", tone: "success" });
    return true;
  };

  const clearSignatureDraft = () => {
    signaturePadRef.current?.clear();
    fullscreenSignatureRef.current?.clear();
    setSignatureFile(null);
    setDrawnSignatureData("");
    setSignaturePreview(originalSignature);
    setUploadMessage({ text: originalSignature ? "Signature changes cleared. Existing signature will be kept." : "", tone: "idle" });
  };

  const undoSignatureStroke = (padRef = signaturePadRef) => {
    const pad = padRef.current;
    if (!pad) return;

    const data = pad.toData();
    if (data.length === 0) return;

    pad.fromData(data.slice(0, -1));
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSignatureFile(null);
    setSignaturePreview("");
    setOriginalSignature("");
    setDrawnSignatureData("");
    setUploadMessage({ text: "", tone: "idle" });
    setEditingEngineerId(null);
    setShowPassword(false);
  };

  const openCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (engineer) => {
    setFormData({
      name: engineer.name || "",
      email: engineer.email || "",
      phone: engineer.phone || "",
      aadhar: engineer.aadhar || "",
      esi: engineer.esi || "",
      password: "",
      team: engineer.team || ""
    });
    setSignatureFile(null);
    setSignaturePreview(engineer.signature || "");
    setOriginalSignature(engineer.signature || "");
    setDrawnSignatureData("");
    setUploadMessage({ text: "", tone: "idle" });
    setEditingEngineerId(engineer._id);

    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === "password" && editingEngineerId && !value) return;
        payload.append(key, value);
      });
      if (drawnSignatureData) {
        payload.append("signatureData", drawnSignatureData);
      } else if (signatureFile) {
        payload.append("signature", signatureFile);
      }

      if (editingEngineerId) {
        const res = await axios.put(
          API_BASE_URL + `/api/engineers/${editingEngineerId}`,
          payload
        );

        setEngineers((prev) =>
          prev.map((engineer) =>
            engineer._id === editingEngineerId ? res.data : engineer
          )
        );

        alert("Engineer updated successfully");
      } else {
        const res = await axios.post(API_BASE_URL + "/api/engineers", payload);

        setEngineers((prev) => [res.data, ...prev]);
        alert("Engineer and login created successfully");
      }

      closeForm();
    } catch (error) {
      console.error("Error saving engineer:", error);
      alert(error.response?.data?.message || "Error saving engineer");
    } finally {
      setSaving(false);
    }
  };

  const toggleCustomerView = async (engineer) => {
    const nextValue = !(engineer.customerViewEnabled ?? true);

    try {
      const res = await axios.put(
        API_BASE_URL + `/api/engineers/${engineer._id}`,
        {
          name: engineer.name || "",
          email: engineer.email || "",
          phone: engineer.phone || "",
          aadhar: engineer.aadhar || "",
          esi: engineer.esi || "",
          customerViewEnabled: nextValue
        }
      );

      setEngineers((current) =>
        current.map((item) => (item._id === engineer._id ? res.data : item))
      );
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update customer visibility");
    }
  };

  const filteredEngineers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return engineers;

    return engineers.filter((engineer) =>
      [
        engineer.name,
        engineer.engineerId,
        engineer.email,
        engineer.phone,
        engineer.aadhar,
        engineer.esi
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [engineers, search]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 lg:mb-6 lg:flex-row lg:items-center lg:justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Engineer Directory</h1>
        <div className="flex w-full gap-2 lg:w-auto">
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search engineer, ID, phone..."
            className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-400 lg:w-80"
          />
          <button type="button" onClick={() => setSearch((current) => current.trim())} className="rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white lg:hidden">
            Search
          </button>
        </div>
        <button
          onClick={openCreateForm}
          className="hidden rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white shadow transition hover:bg-blue-700 lg:block lg:py-2"
        >
          + Add New Engineer
        </button>
      </div>
      <button
        onClick={openCreateForm}
        className="mb-4 w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow transition hover:bg-blue-700 lg:hidden"
      >
        + Add New Engineer
      </button>

      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-lg sm:p-6">
          <div className="mb-4 flex flex-col gap-3 border-b pb-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-bold text-blue-900">
              {editingEngineerId ? "Edit Engineer" : "Register Engineer"}
            </h2>

            <button
              type="button"
              onClick={closeForm}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>

          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">Engineer Name</label>
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                className="border p-2 rounded focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">Email ID</label>
              <input
                type="email"
                name="email"
                placeholder="engineer@company.com"
                value={formData.email}
                onChange={handleChange}
                className="border p-2 rounded focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">
                {editingEngineerId ? "New Password" : "Login Password"}
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder={
                  editingEngineerId
                    ? "Leave blank to keep current password"
                    : "Create login password"
                }
                value={formData.password}
                onChange={handleChange}
                className="border p-2 rounded focus:ring-2 focus:ring-blue-400 outline-none"
                required={!editingEngineerId}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="text-sm text-blue-600 mt-2 text-left hover:underline"
              >
                {showPassword ? "Hide Password" : "View Password"}
              </button>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">Phone Number</label>
              <input
                type="text"
                name="phone"
                placeholder="Mobile Number"
                value={formData.phone}
                onChange={handleChange}
                className="border p-2 rounded focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">Aadhar Number</label>
              <input
                type="text"
                name="aadhar"
                placeholder="12 digit number"
                value={formData.aadhar}
                onChange={handleChange}
                className="border p-2 rounded focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">ESI Number</label>
              <input
                type="text"
                name="esi"
                placeholder="ESI Registration No."
                value={formData.esi}
                onChange={handleChange}
                className="border p-2 rounded focus:ring-2 focus:ring-blue-400 outline-none"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-bold text-gray-700 mb-1">Team</label>
              <select
                name="team"
                value={formData.team}
                onChange={handleChange}
                className="border p-2 rounded focus:ring-2 focus:ring-blue-400 outline-none bg-white h-[42px]"
              >
                <option value="">No Team</option>
                {dynamicTeams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col md:col-span-2">
              <label className="text-sm font-bold text-gray-700 mb-1">Engineer Signature</label>
              <div className="grid gap-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-3">
                  <div>
                    <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500">Draw Signature</p>
                    <div className="overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-white shadow-inner">
                      <SignatureCanvas
                        ref={signaturePadRef}
                        penColor="#111827"
                        minWidth={0.8}
                        maxWidth={2.8}
                        velocityFilterWeight={0.7}
                        canvasProps={{
                          width: 1200,
                          height: 280,
                          className: "h-40 w-full bg-white",
                          style: { touchAction: "none" }
                        }}
                      />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => saveDrawnSignature(signaturePadRef)}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-700"
                      >
                        Save Signature
                      </button>
                      <button
                        type="button"
                        onClick={() => setFullscreenPadOpen(true)}
                        className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-bold text-white hover:bg-slate-800"
                      >
                        Open Fullscreen Pad
                      </button>
                      <button
                        type="button"
                        onClick={() => undoSignatureStroke(signaturePadRef)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-gray-700 hover:bg-gray-100"
                      >
                        Undo
                      </button>
                      <button
                        type="button"
                        onClick={clearSignatureDraft}
                        className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50"
                      >
                        Clear Signature
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500">Or Upload Signature</p>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleSignatureChange}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-blue-700"
                  />
                  <p className="mt-2 text-xs text-gray-500">PNG, JPG, or JPEG. Maximum size 2MB. Transparent PNG is supported.</p>
                  {uploadMessage.text && (
                    <p className={`mt-2 text-xs font-semibold ${uploadMessage.tone === "error" ? "text-red-600" : "text-emerald-600"}`}>
                      {uploadMessage.text}
                    </p>
                  )}
                </div>
                </div>
                {signaturePreview ? (
                  <button
                    type="button"
                    onClick={() => setSignatureModal({ signature: signaturePreview, name: formData.name || "Engineer" })}
                    className="flex min-h-40 w-full items-center justify-center rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <img src={getSignatureUrl(signaturePreview)} alt="Signature preview" className="max-h-36 max-w-full object-contain" />
                  </button>
                ) : (
                  <div className="flex min-h-40 w-full items-center justify-center rounded-lg border border-gray-200 bg-white px-3 text-center text-xs font-semibold text-gray-400">
                    No signature saved yet
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-3 rounded col-span-2 transition shadow-md disabled:bg-gray-400"
            >
              {saving
                ? "Saving..."
                : editingEngineerId
                  ? "Update Engineer Details"
                  : "Save Engineer Details"}
            </button>
          </form>
        </div>
      )}

      <div className="space-y-4 lg:hidden">
        {loading && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-sm">
            Loading engineers...
          </div>
        )}

        {!loading && filteredEngineers.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-sm">
            No engineers added yet
          </div>
        )}

        {!loading && filteredEngineers.map((e) => (
          <article key={e._id || e.engineerId} className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
            <div className="flex justify-between items-start">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">{e.engineerId || "Engineer"}</p>
              {e.team && (
                <span className="text-[10px] bg-sky-100 text-sky-700 font-bold px-2.5 py-0.5 rounded-full">
                  {dynamicTeams.find(t => t.id === e.team || t.name === e.team)?.name || e.team}
                </span>
              )}
            </div>
            <h2 className="mt-2 text-lg font-bold leading-snug text-gray-900">{e.name || "-"}</h2>
            <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">Contact</p>
              <p className="mt-1 break-words text-sm text-gray-700">{e.email || "-"}</p>
              <p className="mt-2 text-sm text-gray-700">{formatPhoneDisplay(e.phone) || "-"}</p>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <InfoPill label="Aadhar" value={e.aadhar || "-"} />
              <InfoPill label="ESI" value={e.esi || "-"} />
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => toggleCustomerView(e)}
                className={`rounded-full px-4 py-2 text-xs font-bold text-white ${(e.customerViewEnabled ?? true) ? "bg-emerald-600" : "bg-red-600"}`}
              >
                Customers {(e.customerViewEnabled ?? true) ? "ON" : "OFF"}
              </button>
              <button
                type="button"
                onClick={() => setSignatureModal({ signature: e.signature || "", name: e.name || e.engineerId || "Engineer" })}
                className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700"
              >
                View Signature
              </button>
              <button
                type="button"
                onClick={() => openEditForm(e)}
                className="rounded-xl bg-yellow-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-yellow-600"
              >
                Edit
              </button>
            </div>
          </article>
        ))}
      </div>

      <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md lg:block">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-100 border-b text-gray-700">
            <tr>
              <th className="p-4 text-left font-bold">Name & ID</th>
              <th className="p-4 text-left font-bold">Email</th>
              <th className="p-4 text-left font-bold">Phone</th>
              <th className="p-4 text-left font-bold">Team</th>
              <th className="p-4 text-left font-bold">Details</th>
              <th className="p-4 text-center font-bold">Engineer Site Access</th>
              <th className="p-4 text-center font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-gray-500">
                  Loading engineers...
                </td>
              </tr>
            )}

            {!loading && filteredEngineers.length === 0 && (
              <tr>
                <td colSpan="7" className="p-6 text-center text-gray-500">
                  No engineers added yet
                </td>
              </tr>
            )}

            {!loading && filteredEngineers.map((e) => (
              <tr key={e._id || e.engineerId} className="hover:bg-gray-50 transition">
                <td className="p-4">
                  <div className="font-bold text-gray-900">{e.name}</div>
                  <div className="text-xs text-blue-600 font-mono">{e.engineerId}</div>
                </td>
                <td className="p-4 text-sm text-gray-600">{e.email || "-"}</td>
                <td className="p-4 text-sm text-gray-600">{formatPhoneDisplay(e.phone) || "-"}</td>
                <td className="p-4 text-sm text-gray-700 font-semibold">
                  {e.team ? (dynamicTeams.find(t => t.id === e.team || t.name === e.team)?.name || e.team) : "No Team"}
                </td>
                <td className="p-4 text-sm">
                  <div className="text-gray-500"><span className="font-bold">A:</span> {e.aadhar || "-"}</div>
                  <div className="text-gray-500"><span className="font-bold">E:</span> {e.esi || "-"}</div>
                </td>
                <td className="p-4 text-center">
                  <button
                    type="button"
                    onClick={() => toggleCustomerView(e)}
                    className={`relative inline-flex h-11 w-28 items-center rounded-full border-2 px-3 text-sm font-black uppercase tracking-wide text-white transition ${
                      (e.customerViewEnabled ?? true)
                        ? "justify-end border-emerald-500 bg-emerald-500 hover:border-emerald-600 hover:bg-emerald-600"
                        : "justify-start border-red-500 bg-red-500 hover:border-red-600 hover:bg-red-600"
                    }`}
                  >
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      {(e.customerViewEnabled ?? true) ? "ON" : "OFF"}
                    </span>
                    <span className="h-8 w-8 rounded-full bg-white shadow-md" />
                  </button>
                </td>
                <td className="p-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEditForm(e)}
                      className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setSignatureModal({ signature: e.signature || "", name: e.name || e.engineerId || "Engineer" })}
                      className="rounded bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                    >
                      View Signature
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {fullscreenPadOpen && (
        <div className="fixed inset-0 z-[60] flex h-[100dvh] flex-col bg-slate-950 text-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
            <div>
              <h2 className="text-base font-bold sm:text-lg">Fullscreen Signature Pad</h2>
              <p className="text-xs text-slate-300">Use finger, stylus, or mouse. Scrolling is disabled while signing.</p>
            </div>
            <button
              type="button"
              onClick={() => setFullscreenPadOpen(false)}
              className="rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/20"
            >
              Close
            </button>
          </div>

          <div className="min-h-0 flex-1 p-3 sm:p-4">
            <div className="h-full overflow-hidden rounded-2xl bg-white shadow-inner">
              <SignatureCanvas
                ref={fullscreenSignatureRef}
                penColor="#111827"
                minWidth={1}
                maxWidth={3.2}
                velocityFilterWeight={0.72}
                canvasProps={{
                  width: 1800,
                  height: 1000,
                  className: "h-full w-full bg-white",
                  style: { touchAction: "none" }
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 px-4 py-3">
            <button
              type="button"
              onClick={() => fullscreenSignatureRef.current?.clear()}
              className="rounded-lg bg-red-500 px-4 py-2 text-sm font-bold text-white hover:bg-red-600"
            >
              Clear Signature
            </button>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => undoSignatureStroke(fullscreenSignatureRef)}
                className="rounded-lg bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/20"
              >
                Undo
              </button>
              <button
                type="button"
                onClick={() => {
                  if (saveDrawnSignature(fullscreenSignatureRef)) {
                    setFullscreenPadOpen(false);
                  }
                }}
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-600"
              >
                Save Signature
              </button>
            </div>
          </div>
        </div>
      )}

      {signatureModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 p-4" onClick={() => setSignatureModal(null)}>
          <div className="w-full max-w-xl rounded-2xl bg-white p-4 shadow-2xl sm:p-6" onClick={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-gray-900">Signature Preview</h2>
              <button
                type="button"
                onClick={() => setSignatureModal(null)}
                className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
            <div className="flex min-h-56 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-4">
              {signatureModal.signature ? (
                <img
                  src={getSignatureUrl(signatureModal.signature)}
                  alt={`${signatureModal.name} signature`}
                  className="max-h-64 max-w-full object-contain"
                />
              ) : (
                <p className="text-sm font-medium text-gray-500">No signature uploaded for this engineer.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoPill({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-2 break-words text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}


